import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Drawer } from "../components/UI";
import { Page } from "../components/AppShell";
import CrudPanel from "../components/CrudPanel";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { DrawerFooter } from "../components/OrgAdmin/_managerShared";

export default function Subjects() {
  const [lookups, setLookups] = useState({});
  const [cases, setCases] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [cloneFor, setCloneFor] = useState(null); // subject row being cloned
  const [pickedJobId, setPickedJobId] = useState("");
  const [cloneErr, setCloneErr] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/subjects/lookups").then(r => setLookups(r.data));
    api.get("/cases").then(r => setCases(r.data));
    api.get("/jobs").then(r => setJobs(r.data));
  }, []);

  const opt = (key) => lookups[key] || [];

  const subjectLabel = (s) =>
    [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(" ").trim() || s.aka || s.id.slice(0, 8);

  const jobLabel = (j) => {
    const c = cases.find(x => x.id === j.case_id);
    const caseTxt = c ? (c.case_number || c.plaintiff || "").trim() : "";
    return `${j.job_number || "JOB-" + j.id.slice(0, 6)}${caseTxt ? "  ·  " + caseTxt : ""}`;
  };

  const openClone = (subject) => {
    setCloneFor(subject);
    setPickedJobId("");
    setCloneErr("");
  };

  const doClone = async () => {
    setCloneErr("");
    if (!pickedJobId) { setCloneErr("Pick a Job to apply this subject to."); return; }
    try {
      await api.post(`/subjects/${cloneFor.id}/clone`, { job_id: pickedJobId });
      setCloneFor(null);
      setPickedJobId("");
      setReloadKey(k => k + 1);
    } catch (e) {
      setCloneErr(formatErr(e));
    }
  };

  return (
    <Page
      title="Subjects"
      subtitle="PERSONS TO BE SERVED / WITNESSES"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Subjects"]}
      testid="subjects-page"
    >
      <CrudPanel
        key={reloadKey}
        endpoint="/subjects"
        testid="subject"
        // eslint-disable-next-line react/no-unstable-nested-components
        rowActions={(row) => (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => openClone(row)}
            data-testid={`apply-subject-${row.id}`}
            title="Apply this subject to another Job — clones name + description into a new linked record"
          >
            <ArrowsClockwise size={12} /> APPLY TO JOB
          </button>
        )}
        columns={[
          { key: "first_name", label: "FIRST" },
          { key: "last_name", label: "LAST" },
          { key: "aka", label: "AKA" },
          { key: "subject_type_id", label: "TYPE", render: r => opt("subject_types").find(o => o.id === r.subject_type_id)?.name || "—" },
          { key: "job_id", label: "JOB", render: r => jobs.find(j => j.id === r.job_id)?.job_number || (r.job_id ? r.job_id.slice(0, 8) : "—") },
          { key: "case_id", label: "CASE", render: r => cases.find(c => c.id === r.case_id)?.case_number || "—" },
        ]}
        formFields={[
          { key: "first_name", label: "FIRST NAME" },
          { key: "middle_name", label: "MIDDLE NAME" },
          { key: "last_name", label: "LAST NAME" },
          { key: "aka", label: "AKA / ALIAS" },
          { key: "date_of_birth", label: "DATE OF BIRTH", type: "date" },
          { key: "ssn_last4", label: "SSN LAST 4" },
          { key: "subject_type_id", label: "TYPE", type: "select", options: opt("subject_types") },
          { key: "case_id", label: "CASE", type: "select", options: cases.map(c => ({ id: c.id, name: c.case_number || c.plaintiff || c.id.slice(0, 8) })) },
          { key: "job_id", label: "JOB", type: "select", options: jobs.map(j => ({ id: j.id, name: j.job_number || j.id.slice(0, 8) })) },
          { key: "gender_id", label: "GENDER", type: "select", options: opt("subject_genders") },
          { key: "race_id", label: "RACE", type: "select", options: opt("subject_races") },
          { key: "hair_color_id", label: "HAIR COLOR", type: "select", options: opt("subject_hair_colors") },
          { key: "eye_color_id", label: "EYE COLOR", type: "select", options: opt("subject_eye_colors") },
          { key: "glasses_id", label: "GLASSES", type: "select", options: opt("subject_glasses") },
          { key: "marital_status_id", label: "MARITAL STATUS", type: "select", options: opt("subject_marital_statuses") },
          { key: "notes", label: "NOTES", type: "textarea" },
        ]}
      />

      <Drawer
        open={!!cloneFor}
        onClose={() => setCloneFor(null)}
        size="narrow"
        testid="subject-clone-drawer"
        title={cloneFor ? `Apply "${subjectLabel(cloneFor)}" to a Job` : ""}
        footer={
          <DrawerFooter
            testid="subject-clone-footer"
            onCancel={() => setCloneFor(null)}
            onSave={doClone}
            mode="create"
            busy={false}
            saveDisabled={!pickedJobId}
            saveLabel="Create linked record"
          />
        }
      >
        <p className="text-xs text-muted-foreground mb-3">
          Clones the subject&apos;s name, AKAs, DOB, physical description, demographics and notes into a brand-new subject row tied to the Job you pick.
          The original record (this row) stays untouched, so each service attempt keeps its own audit trail.
        </p>
        <div className="label-xs mb-1">JOB</div>
        <select
          className="input"
          value={pickedJobId}
          onChange={(e) => setPickedJobId(e.target.value)}
          data-testid="clone-job-picker"
        >
          <option value="">— pick a Job —</option>
          {jobs.map(j => (
            <option key={j.id} value={j.id}>{jobLabel(j)}</option>
          ))}
        </select>
        {cloneErr && <div className="text-xs text-destructive mt-2" data-testid="clone-error">{cloneErr}</div>}
      </Drawer>
    </Page>
  );
}
